// ============================================================
// AdminPromptsPage — M10.8 (PRD §6.1.I R-P0-I3..I5)
// ============================================================
// CRUD Prompts ("dặn dò AI") với scope: ask_ai | chat_ai | both.
// Prompts là system message inject vào LLM tuỳ scope.
//   - List + search + filter scope + toggle activeOnly
//   - Create / Edit (name + content + scope)
//   - Toggle active (bật/tắt)
//   - Re-embed (cost-sensitive → TOTP)
//   - Delete
// ============================================================

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';
// M157 (2026-05-13): TOTP modal cho Tải toàn bộ prompts
import AdminTotpReverifyModal from '../components/AdminTotpReverifyModal.jsx';
// M157b (2026-05-13): Import Excel prompts hàng loạt
import ImportExcelAdminTotpDialog from '../components/ImportExcelAdminTotpDialog.jsx';

const SCOPE_OPTS = [
  { v: '',        l: 'Tất cả scope' },
  { v: 'both',    l: 'Cả hai (Hỏi đáp + Chat AI)' },
  { v: 'ask_ai',  l: 'Chỉ Hỏi đáp (NV)' },
  { v: 'chat_ai', l: 'Chỉ Chat AI (admin)' },
];

const EDIT_SCOPE_OPTS = SCOPE_OPTS.filter(o => o.v);

function scopeLabel(v) {
  const f = SCOPE_OPTS.find(o => o.v === v);
  return f ? f.l : v;
}
function scopeBadgeColor(v) {
  switch (v) {
    case 'both':    return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'ask_ai':  return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'chat_ai': return 'bg-amber-50 text-amber-700 border-amber-200';
    default:        return 'bg-slate-50 text-slate-700 border-slate-200';
  }
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
  } catch { return iso; }
}

export default function AdminPromptsPage() {
  const nav = useNavigate();
  const { user } = useAuthStore();
  // M146: manager với quyền 'prompts' cũng truy cập được
  const isAdmin = (function () {
    if (user?.role === 'admin') return true;
    if (user?.role === 'manager' && Array.isArray(user?.permissions)) return user.permissions.includes('prompts');
    return false;
  })();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [scope, setScope] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [editing, setEditing] = useState(null); // { id?, name, content, scope, active }
  const [embedAllBusy, setEmbedAllBusy] = useState(false);
  // M157 (2026-05-13) — Tải toàn bộ prompts xuống Excel (yêu cầu TOTP admin)
  const [exportTotpOpen, setExportTotpOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  // M157b (2026-05-13) — Import Excel prompts hàng loạt
  const [importExcelOpen, setImportExcelOpen] = useState(false);

  // M17 — count prompts chưa embed (để disable/hiện số trên nút batch)
  const missingCount = items.filter(x => !x.has_embedding).length;

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Chỉ admin mới truy cập được');
      nav('/', { replace: true });
    }
  }, [isAdmin, nav]);

  useEffect(() => {
    if (isAdmin) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, scope, activeOnly]);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.promptsList({
        scope: scope || undefined,
        activeOnly: activeOnly || undefined,
        q: q.trim() || undefined,
      });
      // BE `{rows: [...]}` (legacy promptsService); các service khác dùng `{items}` — accept cả 2 + array thẳng
      const list = Array.isArray(res) ? res : (res.items || res.rows || []);
      setItems(list);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tải được prompts');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing({ id: null, name: '', content: '', scope: 'both', active: true });
  }

  async function openEdit(id) {
    try {
      const row = await api.promptsGet(id);
      setEditing({
        id: row.id,
        name: row.name || '',
        content: row.content || '',
        scope: row.scope || 'both',
        active: !!row.active,
        embedded: !!row.embedding_vector || row.has_embedding,
        updated_at: row.updated_at,
      });
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không lấy được prompt');
    }
  }

  async function save() {
    if (!editing) return;
    const name = editing.name.trim();
    const content = editing.content;
    if (name.length < 3) { toast.error('Tên ≥ 3 ký tự'); return; }
    if (name.length > 200) { toast.error('Tên ≤ 200 ký tự'); return; }
    if (content.length < 10) { toast.error('Nội dung ≥ 10 ký tự'); return; }
    if (content.length > 20000) { toast.error('Nội dung ≤ 20000 ký tự'); return; }
    const body = {
      name, content,
      scope: editing.scope,
      active: editing.id ? editing.active : editing.active,
    };
    try {
      if (editing.id) {
        const res = await api.promptsUpdate(editing.id, body);
        // Feedback chính xác về trạng thái embedding (3 case)
        if (res.contentChanged && res.embedded) {
          toast.success('✓ Đã cập nhật + embed lại thành công', { duration: 4000 });
        } else if (res.contentChanged && !res.embedded) {
          toast.error(
            '⚠ Đã cập nhật nhưng EMBED FAIL — vector cũ đã xoá, prompt hiện "⚠ chưa embed". Bấm "🔄 Embed lại" để thử lại.',
            { duration: 8000 }
          );
        } else {
          // Chỉ đổi name/scope/active, không đụng content → không cần re-embed
          toast.success('Đã cập nhật (không đổi content, giữ nguyên vector)');
        }
      } else {
        const res = await api.promptsCreate(body);
        if (res.embedded) {
          toast.success(`✓ Đã tạo prompt #${res.id} + embed thành công`);
        } else {
          toast.error(
            `⚠ Đã tạo prompt #${res.id} nhưng EMBED FAIL — bấm "🔄 Embed lại" ở list`,
            { duration: 8000 }
          );
        }
      }
      setEditing(null);
      await refresh();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Lưu thất bại');
    }
  }

  async function toggle(id, next) {
    try {
      await api.promptsToggle(id, next);
      toast.success(next ? 'Đã bật' : 'Đã tắt');
      await refresh();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Toggle thất bại');
    }
  }

  async function del(id) {
    const ok = await showConfirm({
      title: 'Xoá prompt',
      message: 'Xoá prompt này?',
      confirmText: 'Xoá',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await api.promptsDelete(id);
      toast.success('Đã xoá');
      await refresh();
      if (editing?.id === id) setEditing(null);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Xoá thất bại');
    }
  }

  async function embedMissingAll() {
    if (embedAllBusy) return;
    if (missingCount === 0) { toast('Tất cả prompts đã embed', { icon: '✓' }); return; }
    const ok = await showConfirm({
      title: 'Embed prompts',
      message: `Embed ${missingCount} prompts chưa có vector?\nƯớc ~${Math.round(missingCount * 0.3)}đ.`,
      confirmText: 'Embed',
    });
    if (!ok) return;
    setEmbedAllBusy(true);
    const t = toast.loading(`Đang embed ${missingCount} prompts…`);
    try {
      const res = await api.promptsEmbedMissing();
      toast.dismiss(t);
      if (res.failed > 0) {
        toast.error(
          `Embed xong: ${res.succeeded}/${res.total} thành công, ${res.failed} fail · ${res.cost_vnd ?? 0}đ`,
          { duration: 7000 }
        );
      } else {
        toast.success(
          `✓ Đã embed ${res.succeeded}/${res.total} prompts · ${res.tokens ?? 0} tok · ${res.cost_vnd ?? 0}đ`,
          { duration: 5000 }
        );
      }
      await refresh();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.data?.error || err.message || 'Embed missing thất bại');
    } finally {
      setEmbedAllBusy(false);
    }
  }

  // ============================================================
  // M157 (2026-05-13) — Tải toàn bộ prompts xuống file JSON.
  // Cost-sensitive (lộ system prompt) → yêu cầu TOTP admin re-verify.
  // ============================================================
  function openExportAll() {
    if (items.length === 0) { toast('Chưa có prompt nào để tải xuống'); return; }
    setExportTotpOpen(true);
  }

  async function handleExportAllTotp(code) {
    setExportBusy(true);
    const t = toast.loading(`Đang xuất ${items.length} prompts…`);
    try {
      const { blob, filename } = await api.promptsExportAll({ adminTotp: code });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.dismiss(t);
      toast.success(`✓ Đã tải xuống ${filename}`);
      setExportTotpOpen(false);
    } catch (err) {
      toast.dismiss(t);
      throw err; // để modal handle TOTP sai
    } finally {
      setExportBusy(false);
    }
  }

  async function embed(id) {
    // M16 (2026-04-23): gỡ TOTP — cost embed 1 prompt ~0.25đ, friction TOTP quá cao.
    try {
      const t = toast.loading('Đang embed...');
      const res = await api.promptsEmbed(id);
      toast.dismiss(t);
      toast.success(
        `Đã embed · ${res.tokens ?? 0} tok · ${res.cost_vnd ?? 0}đ`,
        { duration: 5000 }
      );
      await refresh();
      if (editing?.id === id) await openEdit(id);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Embed thất bại');
    }
  }

  return (
    <div className="min-h-full p-4">
      <div className="max-w-7xl mx-auto">
        {/* HEADER — M148c mobile-first stack */}
        <header className="mb-4">
          <Link
            to="/admin"
            className="text-sm text-slate-500 hover:text-slate-700"
            data-testid="prompts-back"
          >← Trang admin</Link>
          <div className="mt-1 flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg md:text-xl font-semibold text-brand-700">Dặn dò AI (Prompts)</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                System prompt inject vào LLM. Scope quyết định dùng cho Hỏi đáp (NV), Chat AI (admin), hoặc cả hai.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={embedMissingAll}
              disabled={missingCount === 0 || embedAllBusy}
              data-testid="prompts-embed-missing"
              title={missingCount === 0 ? 'Tất cả đã embed' : `Embed ${missingCount} prompts chưa có vector`}
              className="px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {embedAllBusy ? '⏳ Đang embed…' : `⚡ Embed tất cả chưa embed (${missingCount})`}
            </button>
            {/* M157b (2026-05-13): Tải toàn bộ prompts xuống file Excel. Yêu cầu TOTP admin. */}
            <button
              type="button"
              onClick={openExportAll}
              disabled={exportBusy || items.length === 0}
              data-testid="prompts-export-all"
              title={items.length === 0
                ? 'Chưa có prompt để tải xuống'
                : `Tải toàn bộ ${items.length} prompts xuống file Excel (yêu cầu TOTP admin)`}
              className="px-3 py-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {exportBusy ? '⏳ Đang xuất…' : `⬇ Tải Excel (${items.length})`}
            </button>
            {/* M157b (2026-05-13): Import hàng loạt prompts từ file Excel. Yêu cầu TOTP admin. */}
            <button
              type="button"
              onClick={() => setImportExcelOpen(true)}
              data-testid="prompts-import-excel"
              title="Import hàng loạt prompts từ file Excel (yêu cầu TOTP admin)"
              className="px-3 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 text-sm font-medium"
            >
              📤 Import Excel
            </button>
            <button
              type="button"
              onClick={openCreate}
              data-testid="prompts-new"
              className="px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold shadow-sm"
            >➕ Thêm dặn dò</button>
          </div>
        </header>

        {/* FILTERS */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 mb-4 flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') refresh(); }}
            placeholder="Tìm theo tên hoặc nội dung..."
            className="flex-1 min-w-[200px] rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm"
            data-testid="prompts-search"
          />
          <select
            value={scope}
            onChange={e => setScope(e.target.value)}
            className="rounded-lg border border-slate-300 p-2 text-sm"
            data-testid="prompts-scope-filter"
          >
            {SCOPE_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <label className="inline-flex items-center gap-1 text-xs text-slate-600 px-2 py-2 rounded bg-slate-50 border border-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={e => setActiveOnly(e.target.checked)}
              className="accent-brand-600"
              data-testid="prompts-active-only"
            />
            <span>Chỉ active</span>
          </label>
          <button
            type="button"
            onClick={refresh}
            className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm"
            data-testid="prompts-refresh"
          >🔍 Tìm</button>
        </div>

        {/* LIST — M148c hotfix: overflow-x-auto cho phép scroll ngang table trên mobile */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-x-auto">
          {loading && <div className="p-6 text-center text-sm text-slate-400">Đang tải…</div>}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-400">
              Chưa có prompt nào. Bấm "➕ Thêm dặn dò" để tạo mới.
            </div>
          )}
          {!loading && items.length > 0 && (
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="text-left py-2 px-3 font-semibold">#</th>
                  <th className="text-left py-2 px-3 font-semibold">Tên</th>
                  <th className="text-left py-2 px-3 font-semibold">Scope</th>
                  <th className="text-center py-2 px-3 font-semibold">Active</th>
                  <th className="text-center py-2 px-3 font-semibold">Embed</th>
                  <th className="text-left py-2 px-3 font-semibold">Cập nhật</th>
                  <th className="text-right py-2 px-3 font-semibold">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {items.map(row => {
                  const embedded = !!row.has_embedding || !!row.embedding_vector;
                  return (
                    <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`prompts-row-${row.id}`}>
                      <td className="py-2 px-3 text-slate-400 font-mono text-xs">#{row.id}</td>
                      <td className="py-2 px-3 max-w-xs">
                        <div className="font-medium text-slate-800 truncate">{row.name}</div>
                        <div className="text-[11px] text-slate-400 truncate">
                          {(row.content || '').slice(0, 80)}{(row.content || '').length > 80 ? '…' : ''}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`text-[11px] px-2 py-0.5 rounded border ${scopeBadgeColor(row.scope)}`}>
                          {scopeLabel(row.scope)}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <button
                          type="button"
                          onClick={() => toggle(row.id, !row.active)}
                          className={`text-xs px-2 py-1 rounded border ${
                            row.active
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                              : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                          }`}
                          data-testid={`prompts-toggle-${row.id}`}
                        >
                          {row.active ? '🟢 Bật' : '⚪ Tắt'}
                        </button>
                      </td>
                      <td className="py-2 px-3 text-center">
                        {embedded ? (
                          <span className="text-xs text-emerald-600">✓</span>
                        ) : (
                          <span className="text-xs text-amber-600">⚠ chưa</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500">{fmtTime(row.updated_at)}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openEdit(row.id)}
                          className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                          data-testid={`prompts-edit-${row.id}`}
                        >✏️ Sửa</button>
                        <button
                          type="button"
                          onClick={() => embed(row.id)}
                          className="text-xs px-2 py-1 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 ml-1"
                          title="Re-embed (OTP)"
                          data-testid={`prompts-embed-${row.id}`}
                        >🔄 Embed</button>
                        <button
                          type="button"
                          onClick={() => del(row.id)}
                          className="text-xs px-2 py-1 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 ml-1"
                          data-testid={`prompts-delete-${row.id}`}
                        >🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* EDIT MODAL */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
          data-testid="prompts-edit-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              {editing.id ? `✏️ Sửa dặn dò #${editing.id}` : '➕ Thêm dặn dò mới'}
            </h2>
            {editing.id && (
              <div className="text-[11px] text-slate-500 mb-3">
                {editing.embedded ? '✓ Đã embed' : '⚠ Chưa embed'} · Cập nhật: {fmtTime(editing.updated_at)}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Tên (3–200)</label>
                <input
                  type="text"
                  value={editing.name}
                  onChange={e => setEditing({ ...editing, name: e.target.value })}
                  maxLength={200}
                  className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm"
                  data-testid="prompts-name"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Scope (phạm vi áp dụng)</label>
                <div className="flex gap-2 text-xs flex-wrap">
                  {EDIT_SCOPE_OPTS.map(o => (
                    <label
                      key={o.v}
                      className={`flex items-center gap-1 px-2 py-1 rounded border cursor-pointer ${
                        editing.scope === o.v ? 'bg-brand-50 border-brand-300 text-brand-800' : 'bg-white border-slate-200 text-slate-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="scope-edit"
                        value={o.v}
                        checked={editing.scope === o.v}
                        onChange={() => setEditing({ ...editing, scope: o.v })}
                        className="accent-brand-600"
                      />
                      <span>{o.l}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Nội dung ({editing.content.length}/20000)
                </label>
                <textarea
                  value={editing.content}
                  onChange={e => setEditing({ ...editing, content: e.target.value })}
                  rows={12}
                  placeholder="Ví dụ: Luôn trả lời bằng tiếng Việt. Khi khách hỏi về bảo hành, phải nhắc đến thời hạn 12 tháng..."
                  className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-xs"
                  data-testid="prompts-content"
                />
              </div>

              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={e => setEditing({ ...editing, active: e.target.checked })}
                  className="accent-brand-600"
                  data-testid="prompts-active"
                />
                <span className="text-slate-700">Active (dùng để inject vào AI)</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setEditing(null)}
                data-testid="prompts-cancel"
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
              >Huỷ</button>
              {editing.id && (
                <button
                  onClick={() => embed(editing.id)}
                  data-testid="prompts-embed-from-modal"
                  className="px-4 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-sm font-medium"
                >🔄 Embed lại</button>
              )}
              <button
                onClick={save}
                data-testid="prompts-save"
                className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold"
              >{editing.id ? 'Lưu' : 'Tạo mới'}</button>
            </div>
          </div>
        </div>
      )}

      {/* M157b (2026-05-13): TOTP modal cho Tải toàn bộ prompts ra Excel */}
      <AdminTotpReverifyModal
        open={exportTotpOpen}
        title="Xác minh để tải toàn bộ dặn dò AI ra Excel"
        description={`Tải xuống ${items.length} prompts ra file Excel (.xlsx).\nNội dung system prompt sẽ nằm trong file — bảo quản cẩn thận.`}
        onClose={() => setExportTotpOpen(false)}
        onSubmit={handleExportAllTotp}
      />

      {/* M157b (2026-05-13): Import Excel prompts */}
      <ImportExcelAdminTotpDialog
        open={importExcelOpen}
        title="Import dặn dò AI từ Excel"
        formatHelp={
          <>
            <div className="font-medium text-slate-800 mb-1">Định dạng yêu cầu</div>
            <div>File <code>.xlsx</code>, sheet đầu tiên, header hàng 1 gồm các cột:</div>
            <ul className="list-disc ml-5 mt-1 text-slate-700">
              <li><b>Tên</b> (bắt buộc, 3-200 ký tự)</li>
              <li><b>Scope</b> (bắt buộc: <code>ask_ai</code> / <code>chat_ai</code> / <code>both</code>)</li>
              <li><b>Active</b> (tuỳ chọn: 0/1, mặc định 1)</li>
              <li><b>Nội dung</b> (bắt buộc, 10-20000 ký tự)</li>
            </ul>
            <div className="mt-1 text-slate-500">
              Prompt trùng tên với DB hoặc trong file sẽ bị <b>skip</b> (không ghi đè).
              Prompt mới sẽ được auto-embed sau khi tạo.
            </div>
          </>
        }
        onClose={() => setImportExcelOpen(false)}
        onDone={() => { refresh(); }}
        uploadFn={(file, totp) => api.promptsImportExcel(file, totp)}
      />
    </div>
  );
}
